import { useEffect, useRef, useState } from "react"
import { apiClient } from "@/api/client"

export type CpLookupStatus = "idle" | "loading" | "found" | "notfound"

export interface ResolvedPostalCode {
  state: string
  city: string
  colonias: string[]
}

/**
 * Looks up a Mexican CP (5 digits) against the SEPOMEX dataset and reports the
 * resolved colonias + status. Calls `onResolved` once per successful lookup so the
 * caller can autofill state/city. Dedupes by CP and ignores stale responses.
 */
export function usePostalCodeAutofill(
  zipCode: string | undefined,
  onResolved: (info: ResolvedPostalCode) => void
) {
  const [colonias, setColonias] = useState<string[]>([])
  const [status, setStatus] = useState<CpLookupStatus>("idle")
  const lastCp = useRef<string | null>(null)
  // Keep the latest callback without re-triggering the effect on each render.
  const onResolvedRef = useRef(onResolved)
  onResolvedRef.current = onResolved

  useEffect(() => {
    const cp = (zipCode ?? "").trim()
    if (!/^\d{5}$/.test(cp)) {
      lastCp.current = null
      setColonias([])
      setStatus("idle")
      return
    }
    if (cp === lastCp.current) return
    lastCp.current = cp

    let cancelled = false
    setStatus("loading")
    apiClient
      .getPostalCodeInfo(cp)
      .then((info) => {
        if (cancelled) return
        const list = info.colonias ?? []
        setColonias(list)
        setStatus("found")
        onResolvedRef.current({ state: info.state, city: info.city, colonias: list })
      })
      .catch(() => {
        if (cancelled) return
        setColonias([])
        setStatus("notfound")
      })

    return () => {
      cancelled = true
    }
  }, [zipCode])

  return { colonias, status }
}
